import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { QRCodeSVG } from "qrcode.react";
import IDaily_Route from "../../../models/IDaily_Route";
import IDaily_Route_Order_Map from "../../../models/IDaily_Route_Order_Map";
import IOrder from "../../../models/IOrder";
import IVendorModel from "../../../models/IVendorModel";
import IAddress from "../../../models/IAddress";
import { useGoogleMaps } from "../../../hooks/shared/useGoogleMaps";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwButton from "../../shared/OwButton/OwButton";
import { ClickToCopy } from "../../shared/ClickToCopy/ClickToCopy";
import "./DailyRouteMapModal.scss";
import { useAppContextNonNull } from "@contexts/AppContext";
import DailyRouteService from "../../../services/DailyRouteService";

interface IWaypoint {
    isOrigin: boolean;
    order?: IOrder;
    // Order-less customer stop (van-sell DSD): identity/address come from the stop-level Vendor
    vendor?: IVendorModel;
    address: IAddress;
    stopNumber: number;
    location: string;
}

type Props = {
    dailyRoute: IDaily_Route | null;
    show: boolean;
    onHide: () => void;
};

// Helper component to render DirectionsRenderer (must be inside Map)
const DirectionsRendererComponent = ({ directions }: { directions: google.maps.DirectionsResult }) => {
    const map = useMap();
    const [renderer, setRenderer] = useState<google.maps.DirectionsRenderer | null>(null);

    useEffect(() => {
        if (!map) return;

        const directionsRenderer = new google.maps.DirectionsRenderer({
            map,
            suppressMarkers: true,
            polylineOptions: {
                strokeColor: "#4285F4",
                strokeWeight: 5,
                strokeOpacity: 0.8,
            },
        });

        setRenderer(directionsRenderer);

        return () => {
            directionsRenderer.setMap(null);
        };
    }, [map]);

    useEffect(() => {
        if (renderer && directions) {
            renderer.setDirections(directions);
        }
    }, [renderer, directions]);

    return null;
};

// Inner map content component (must be inside APIProvider/Map)
interface MapContentProps {
    waypoints: IWaypoint[];
    selectedMarker: IWaypoint | null;
    onMarkerClick: (wp: IWaypoint) => void;
    onInfoWindowClose: () => void;
    onDirectionsLoaded: (directions: google.maps.DirectionsResult) => void;
    onError: (error: string) => void;
    onLoadingComplete: () => void;
    supplierName: string;
}

const MapContent = ({
    waypoints,
    selectedMarker,
    onMarkerClick,
    onInfoWindowClose,
    onDirectionsLoaded,
    onError,
    onLoadingComplete,
    supplierName,
}: MapContentProps) => {
    const map = useMap();
    const routesLibrary = useMapsLibrary("routes");
    const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
    const directionsRequestedRef = useRef(false);

    // Reset ref when waypoints change (new route)
    useEffect(() => {
        directionsRequestedRef.current = false;
    }, [waypoints]);

    // Request directions when library loads
    useEffect(() => {
        // Prevent duplicate requests
        if (directionsRequestedRef.current) return;
        if (!routesLibrary || waypoints.length < 2) {
            onLoadingComplete();
            return;
        }

        directionsRequestedRef.current = true;
        const directionsService = new routesLibrary.DirectionsService();

        // Build waypoints for Google (exclude first and last as they are origin/destination)
        const googleWaypoints = waypoints.slice(1, -1).map((wp) => ({
            location: wp.location,
            stopover: true,
        }));

        directionsService
            .route({
                origin: waypoints[0].location,
                destination: waypoints[waypoints.length - 1].location,
                waypoints: googleWaypoints,
                travelMode: google.maps.TravelMode.DRIVING,
                optimizeWaypoints: false, // Already optimized
            })
            .then((result) => {
                setDirections(result);
                onDirectionsLoaded(result);
                onLoadingComplete();

                // Fit bounds to route
                if (map && result.routes[0]?.bounds) {
                    map.fitBounds(result.routes[0].bounds);
                }
            })
            .catch((error) => {
                console.error("Directions request failed:", error);
                onError(`Failed to load route: ${error.message || error}`);
                onLoadingComplete();
            });
    }, [routesLibrary, waypoints, map, onDirectionsLoaded, onError, onLoadingComplete]);

    // Get marker position from directions result
    const getMarkerPosition = (index: number): google.maps.LatLngLiteral | null => {
        if (!directions || !directions.routes[0]?.legs) return null;

        const legs = directions.routes[0].legs;

        // First waypoint (origin) - start of first leg
        if (index === 0) {
            return legs[0].start_location.toJSON();
        }

        // Last waypoint - end of last leg
        if (index === waypoints.length - 1) {
            return legs[legs.length - 1].end_location.toJSON();
        }

        // Middle waypoints - end of leg at index-1
        if (index - 1 < legs.length) {
            return legs[index - 1].end_location.toJSON();
        }

        return null;
    };

    return (
        <>
            {/* Render the driving route */}
            {directions && <DirectionsRendererComponent directions={directions} />}

            {/* Render numbered markers */}
            {directions &&
                waypoints.map((wp, index) => {
                    const position = getMarkerPosition(index);
                    if (!position) return null;

                    return (
                        <AdvancedMarker
                            key={wp.isOrigin ? "origin" : wp.order?.guid || wp.vendor?.guid}
                            position={position}
                            onClick={() => onMarkerClick(wp)}
                            title={wp.isOrigin ? "Start" : `Stop ${wp.stopNumber}`}>
                            <div
                                style={{
                                    width: 30,
                                    height: 30,
                                    background: wp.isOrigin ? "#34A853" : "#EA4335",
                                    borderRadius: "50%",
                                    border: "2px solid white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    fontWeight: "bold",
                                    fontSize: "12px",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                    cursor: "pointer",
                                }}>
                                {wp.isOrigin ? "S" : wp.stopNumber}
                            </div>
                        </AdvancedMarker>
                    );
                })}

            {/* Info window for selected marker */}
            {selectedMarker && directions && (
                <InfoWindow position={getMarkerPosition(waypoints.indexOf(selectedMarker)) || undefined} onCloseClick={onInfoWindowClose}>
                    <div className="info-window-content">
                        {selectedMarker.isOrigin ? (
                            <>
                                <strong>
                                    <OwLabel title="start" /> - {supplierName}
                                </strong>
                                <p className="mb-0 mt-1">{selectedMarker.address.address_line_one}</p>
                                <p className="mb-0">
                                    {selectedMarker.address.city}, {selectedMarker.address.state}
                                </p>
                            </>
                        ) : (
                            <>
                                <strong>
                                    <OwLabel title="stop" /> {selectedMarker.stopNumber}
                                </strong>
                                <p className="mb-0 mt-1 fw-bold">{selectedMarker.order?.name || selectedMarker.vendor?.name}</p>
                                {selectedMarker.order?.Vendor && (
                                    <p className="mb-0 text-muted small">{selectedMarker.order.Vendor.name}</p>
                                )}
                                <p className="mb-0 mt-1">{selectedMarker.address.address_line_one}</p>
                                <p className="mb-0">
                                    {selectedMarker.address.city}, {selectedMarker.address.state}
                                </p>
                            </>
                        )}
                    </div>
                </InfoWindow>
            )}
        </>
    );
};

export const DailyRouteMapModal = (props: Props) => {
    const { dailyRoute, show, onHide } = props;
    const { supplierModel } = useAppContextNonNull();
    const { getGoogleMapsAPIKey } = useGoogleMaps({});

    const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
    const [waypoints, setWaypoints] = useState<IWaypoint[]>([]);
    const [selectedMarker, setSelectedMarker] = useState<IWaypoint | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showQrModal, setShowQrModal] = useState(false);

    // Memoized callbacks to prevent useEffect re-runs in MapContent
    const handleDirectionsLoaded = useCallback((result: google.maps.DirectionsResult) => {
        setDirections(result);
    }, []);

    const handleError = useCallback((err: string) => {
        setError(err);
    }, []);

    const handleLoadingComplete = useCallback(() => {
        setIsLoading(false);
    }, []);

    const handleInfoWindowClose = useCallback(() => {
        setSelectedMarker(null);
    }, []);

    const buildWaypoints = useCallback(() => {
        if (!dailyRoute || !supplierModel.Ship_To) {
            return [];
        }

        const maps = dailyRoute.Daily_Route_Order_Maps || [];

        // Sort by optimized sequence (null/-1 last, name tiebreak — matches DailyRoutes and
        // DailyRouteSidebar); order-less customer stops use the stop-level Vendor's address
        const seqOf = (map: IDaily_Route_Order_Map) =>
            map.optimized_order_seq === null || map.optimized_order_seq === undefined || map.optimized_order_seq === -1
                ? Number.MAX_SAFE_INTEGER
                : map.optimized_order_seq;
        const nameOf = (map: IDaily_Route_Order_Map) => map.Order?.name || map.Vendor?.name || "";

        const sorted = [...maps]
            .filter((m) => m.Order?.Ship_To || m.Vendor?.Ship_To)
            .sort((a, b) => seqOf(a) - seqOf(b) || nameOf(a).localeCompare(nameOf(b)));

        // Origin is always the supplier's Ship To address
        const supplierShipTo = supplierModel.Ship_To;
        const origin: IWaypoint = {
            isOrigin: true,
            address: supplierShipTo,
            stopNumber: 0,
            location: `${supplierShipTo.address_line_one}, ${supplierShipTo.city}, ${supplierShipTo.state}`,
        };

        const stops: IWaypoint[] = sorted.map((map, index) => {
            const address = (map.Order?.Ship_To || map.Vendor!.Ship_To)!;

            return {
                isOrigin: false,
                order: map.Order,
                vendor: map.Vendor,
                address,
                stopNumber: index + 1,
                location: `${address.address_line_one}, ${address.city}, ${address.state}`,
            };
        });

        return [origin, ...stops];
    }, [dailyRoute, supplierModel.Ship_To]);

    // Generate Google Maps URL for sharing
    const googleMapsUrl = useMemo(() => {
        if (waypoints.length < 2) return "";

        const origin = encodeURIComponent(waypoints[0].location);
        const destination = encodeURIComponent(waypoints[waypoints.length - 1].location);

        // Middle waypoints (if any)
        const middleWaypoints = waypoints
            .slice(1, -1)
            .map((wp) => encodeURIComponent(wp.location))
            .join("|");

        let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;

        if (middleWaypoints) {
            url += `&waypoints=${middleWaypoints}`;
        }

        return url;
    }, [waypoints]);

    // Sum of per-stop service (dwell) minutes (stop override -> customer default
    // -> supplier default). Kept separate from total_time, which is driving-only.
    const totalServiceMinutes = useMemo(() => {
        const maps = dailyRoute?.Daily_Route_Order_Maps || [];
        return maps.reduce(
            (sum, map) =>
                sum +
                DailyRouteService.getEffectiveServiceMinutes(
                    map.service_minutes,
                    supplierModel.default_stop_service_minutes,
                    map.Order?.Vendor?.default_service_minutes ?? map.Vendor?.default_service_minutes
                ),
            0
        );
    }, [dailyRoute, supplierModel.default_stop_service_minutes]);

    // Build waypoints when modal opens
    useEffect(() => {
        if (show && dailyRoute) {
            const waypointList = buildWaypoints();
            setWaypoints(waypointList);
            setIsLoading(true);
            setError(null);
            setDirections(null);
            setSelectedMarker(null);
        }
    }, [show, dailyRoute, buildWaypoints]);

    // Reset state when modal closes
    useEffect(() => {
        if (!show) {
            setDirections(null);
            setWaypoints([]);
            setSelectedMarker(null);
            setIsLoading(true);
            setError(null);
        }
    }, [show]);

    if (!dailyRoute) return null;

    return (
        <Modal show={show} onHide={onHide} size="xl" className="DailyRouteMapModal" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <OwLabel title="optimized_route_map" />
                    {dailyRoute.Route?.name && <span className="ms-2">- {dailyRoute.Route.name}</span>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading && (
                    <div className="text-center py-5">
                        <i className="fas fa-spinner fa-spin fa-2x mb-3" />
                        <div>
                            <OwLabel title="loading_map" />
                        </div>
                    </div>
                )}
                {error && (
                    <div className="alert alert-danger">
                        <i className="fas fa-exclamation-triangle me-2" />
                        {error}
                    </div>
                )}

                <APIProvider apiKey={getGoogleMapsAPIKey()}>
                    <Map
                        style={{ width: "100%", height: "500px" }}
                        defaultCenter={{ lat: 39.8283, lng: -98.5795 }}
                        defaultZoom={10}
                        gestureHandling="greedy"
                        mapId="daily-route-map">
                        {waypoints.length > 0 && (
                            <MapContent
                                waypoints={waypoints}
                                selectedMarker={selectedMarker}
                                onMarkerClick={setSelectedMarker}
                                onInfoWindowClose={handleInfoWindowClose}
                                onDirectionsLoaded={handleDirectionsLoaded}
                                onError={handleError}
                                onLoadingComplete={handleLoadingComplete}
                                supplierName={supplierModel.name}
                            />
                        )}
                    </Map>
                </APIProvider>

                {/* Route summary */}
                {directions && directions.routes[0]?.legs && (
                    <div className="route-summary mt-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <strong>
                                    <OwLabel title="total_stops" />:
                                </strong>{" "}
                                {waypoints.length - 1}
                            </div>
                            <div>
                                <strong>
                                    <OwLabel title="total_distance" />:
                                </strong>{" "}
                                {directions.routes[0].legs.reduce((sum, leg) => sum + (leg.distance?.value || 0), 0) / 1609.34 > 0
                                    ? `${(directions.routes[0].legs.reduce((sum, leg) => sum + (leg.distance?.value || 0), 0) / 1609.34).toFixed(1)} mi`
                                    : "N/A"}
                            </div>
                            <div>
                                <strong>
                                    <OwLabel title="total_time" />:
                                </strong>{" "}
                                {Math.round(directions.routes[0].legs.reduce((sum, leg) => sum + (leg.duration?.value || 0), 0) / 60)} min
                            </div>
                            <div>
                                <strong>
                                    <OwLabel title="total_service_time" />:
                                </strong>{" "}
                                {totalServiceMinutes} min
                            </div>
                        </div>

                        {/* Google Maps share section */}
                        {googleMapsUrl && (
                            <div className="mt-3 pt-3 border-top">
                                <div className="d-flex align-items-center gap-4">
                                    {/* QR Code - clickable to enlarge */}
                                    <div
                                        className="text-center"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => setShowQrModal(true)}
                                        title="Click to enlarge">
                                        <QRCodeSVG value={googleMapsUrl} size={100} level="M" includeMargin={false} />
                                        <div className="small text-muted mt-1">
                                            <OwLabel title="click_to_enlarge" />
                                        </div>
                                    </div>

                                    {/* Copy link */}
                                    <div className="flex-grow-1">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="fab fa-google text-primary" />
                                            <ClickToCopy text={googleMapsUrl} label="Copy Google Maps link" />
                                        </div>
                                        <div className="small text-muted mt-1">
                                            <OwLabel title="open_route_in_google_maps_description" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* QR Code Enlarged Modal */}
                        <Modal className="DailyRouteMapModal" show={showQrModal} onHide={() => setShowQrModal(false)} centered>
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    <OwLabel title="scan_to_open_in_google_maps" />
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body className="text-center py-4">
                                <QRCodeSVG value={googleMapsUrl} size={280} level="M" includeMargin />
                            </Modal.Body>
                        </Modal>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <OwButton title="close" className="btn-secondary" fn={onHide} />
            </Modal.Footer>
        </Modal>
    );
};
